import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import useSEO from "@/hooks/useSEO";
import { useTrackPageView } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import MarqueeRibbon from "@/components/books/MarqueeRibbon";
import CollectionCard from "@/components/books/CollectionCard";
import PrintInterestButton from "@/components/books/PrintInterestButton";
import BookCover from "@/components/books/BookCover";
import { SERIES, CHINNU_BOOKS, LIFEIQ_BOOKS_MANIFESTO, LIFEIQ_LIBRARY_CONCEPTS } from "@/data/books";
import { CYBER_BOOKS, CYBER_SAFETY_SERIES, CYBER_SAFETY_ACCENT as CC } from "@/data/cyberSafetyBooks";
import { LEVEL3_BOOKS, LEVEL3_SERIES, LEVEL3_ACCENT as L3 } from "@/data/level3Books";

const ANCHOR_NAV = [
  { id: "library", label: "All Series" },
  { id: "chinnu", label: "Chinnu" },
  { id: "level-1", label: "Level 1" },
  { id: "level-2", label: "Level 2" },
  { id: "level3-collection", label: "Level 3" },
  { id: "cyber-safety", label: "Cyber Safety" },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const lenis = window.__booksLenis;
  if (lenis) lenis.scrollTo(el, { offset: -20 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Books() {
  useTrackPageView("books_page_view");
  useSEO({
    title: "LifeIQ Books — Real-world books for every stage of growing up",
    description: "LifeIQ Publications' library of real-world life-skills books for children, teens, young adults and families — Chinnu Talking Adventures in printed book form, plus LifeIQ Cyber Fraud & Scam Safety and LifeIQ Level 3 · Life Design digital editions, and more.",
    canonicalPath: "/books",
  });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const stackY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const wordY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const level1 = SERIES.find((s) => s.id === "lifeiq-l1");
  const level2 = SERIES.find((s) => s.id === "lifeiq-l2");

  // Restrained, real-cover-only representation of all THREE active
  // collection families (no invented Level 1/2 covers). Command 02 §3.
  const heroCovers = [CHINNU_BOOKS[0], CYBER_BOOKS[0], LEVEL3_BOOKS[0], CHINNU_BOOKS[1], CYBER_BOOKS[1]];
  const heroCoversMobile = [CHINNU_BOOKS[0], CYBER_BOOKS[0], LEVEL3_BOOKS[0]];

  return (
    <main>
      {/* HERO — permanent publishing-store hero (Command 02 §2) */}
      <section ref={heroRef} className="relative bg-[#0D1B2A] text-white overflow-hidden">
        <span
          aria-hidden="true"
          className="font-telugu absolute -top-10 right-0 text-[22vw] leading-none text-transparent pointer-events-none select-none text-outline-cream opacity-60"
        >
          పుస్తకాలు
        </span>

        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-24 md:pt-32 pb-24 md:pb-36 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <motion.div className="lg:col-span-7" style={{ y: wordY }}>
              <HeroFade delay={0}>
                <div className="text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">LifeIQ Books</div>
              </HeroFade>
              <KineticTitle
                testId="books-hero-title"
                className="mt-6 font-serif-d text-[40px] sm:text-6xl lg:text-[68px] leading-[1.06] tracking-tight text-white"
                lines={["Real-world books", "for every stage", "of growing up."]}
              />
              <HeroFade delay={0.55}>
                <p className="mt-6 text-[16px] sm:text-[18px] text-white/80 leading-[1.7] max-w-2xl">
                  Practical books for children, young readers, teens, young adults and families — built around communication, life skills, digital awareness, money, decisions and everyday capability.
                </p>
                <div data-testid="books-hero-format-line" className="mt-4 flex items-center gap-3 text-[12px] sm:text-[13px] uppercase tracking-[0.14em] text-white/55 font-semibold flex-wrap">
                  <span>Select Digital Editions Available Now</span>
                  <span className="text-[#F57F17]">·</span>
                  <span>More Series Coming</span>
                  <span className="text-[#F57F17]">·</span>
                  <span>Printed Editions Planned</span>
                </div>
                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    data-testid="books-hero-explore-library-cta"
                    onClick={() => scrollToId("library")}
                    className="inline-flex items-center justify-center gap-2 bg-[#F57F17] text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-[#D86B0F] transition-colors duration-200 rounded-sm"
                  >
                    Explore the Library <ArrowDown size={15} />
                  </button>
                  <PrintInterestButton
                    mode="multi"
                    sourcePage="books-hero"
                    label="Interested in Printed Books?"
                    testId="books-hero-print-interest-cta"
                    accentColor="#F57F17"
                    className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/40 px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-white/10 transition-colors duration-200 rounded-sm"
                  />
                </div>
              </HeroFade>
            </motion.div>

            {/* Restrained multi-collection cover composition — real covers only */}
            <motion.div className="lg:col-span-5" style={{ y: stackY }}>
              <HeroFade delay={0.4} className="relative h-[420px] sm:h-[480px] hidden sm:block">
                {heroCovers.map((b, i) => (
                  <motion.div
                    key={`${b.slug}-${i}`}
                    className="absolute w-[27%]"
                    style={{
                      left: `${i * 17}%`,
                      top: `${(i % 2) * 15 + 5}%`,
                      rotate: (i % 2 ? 1 : -1) * (3 + i),
                      zIndex: i,
                    }}
                    initial={{ opacity: 0, y: 60, rotate: 0 }}
                    animate={{ opacity: 1, y: 0, rotate: (i % 2 ? 1 : -1) * (3 + i) }}
                    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.55 + i * 0.12 }}
                  >
                    <BookCover book={b} testId={`books-hero-cover-${b.slug}`} />
                  </motion.div>
                ))}
              </HeroFade>
              {/* mobile: one representative cover per active series */}
              <div className="grid grid-cols-3 gap-3 sm:hidden">
                {heroCoversMobile.map((b) => (
                  <BookCover key={b.slug} book={b} testId={`books-hero-cover-${b.slug}`} />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EDITORIAL MARQUEE — broad concepts, not a per-book ticker (§4) */}
      <MarqueeRibbon tone="dark" items={LIFEIQ_LIBRARY_CONCEPTS} />

      {/* EXPLORE THE LIFEIQ LIBRARY — equal-importance collection grid (§5/§6) */}
      <section id="library" className="bg-[#FBF7EE]">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] text-[#004D40] font-semibold">The catalogue</div>
            <h2 className="mt-4 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08] text-[#0D1B2A] max-w-3xl">
              Explore the LifeIQ Library.
            </h2>
            <p className="mt-5 text-[16px] sm:text-[17px] text-[#5C6470] leading-[1.7] max-w-2xl">
              Different stages. Different situations. The same goal: helping readers become more capable in real life.
            </p>
          </Reveal>

          <nav aria-label="Jump to a collection" className="mt-10 flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {ANCHOR_NAV.map((a) => (
              <button
                key={a.id}
                type="button"
                data-testid={`books-anchor-nav-${a.id}`}
                onClick={() => scrollToId(a.id)}
                className="shrink-0 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] rounded-full border border-[#0D1B2A]/15 text-[#0D1B2A]/70 hover:border-[#0D1B2A]/40 hover:text-[#0D1B2A] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F57F17]"
              >
                {a.label}
              </button>
            ))}
          </nav>

          <div className="mt-10 flex flex-wrap justify-center gap-6">
            <Reveal className="w-full sm:w-[calc(50%-12px)] lg:w-[calc((100%-48px)/3)]">
              <div id="chinnu">
                <CollectionCard
                  testId="collection-card-chinnu"
                  name="Chinnu Talking Adventures"
                  descriptor="Bilingual English–Telugu communication books"
                  languageBadge="ENGLISH + తెలుగు"
                  covers={CHINNU_BOOKS.slice(0, 3)}
                  format="Printed Book"
                  availability="available_print"
                  stat="4 Printed Books"
                  priceIndicator="Printed Books from ₹249 · Complete Collection ₹999"
                  accent={{ bg: "#FFFFFF", primary: "#F57F17", text: "#0D1B2A", textMuted: "#5C6470", border: "#E7DFCF" }}
                  primaryHref="/books/chinnu"
                  primaryLabel="Explore Chinnu"
                />
              </div>
            </Reveal>

            <Reveal delay={0.05} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc((100%-48px)/3)]">
              <div id="level-1">
                <CollectionCard
                  testId="collection-card-level1"
                  name={level1.name}
                  levelNumber={1}
                  descriptor={level1.retail_descriptor}
                  covers={[]}
                  availability="coming_soon"
                  accent={{ bg: "#FFFFFF", primary: "#004D40", text: "#0D1B2A", textMuted: "#5C6470", border: "#E7DFCF" }}
                  releaseInterest={{ seriesId: "lifeiq-level-1", seriesName: level1.name, sourcePage: "books-library-level1" }}
                />
              </div>
            </Reveal>

            <Reveal delay={0.1} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc((100%-48px)/3)]">
              <div id="level-2">
                <CollectionCard
                  testId="collection-card-level2"
                  name={level2.name}
                  levelNumber={2}
                  descriptor={level2.retail_descriptor}
                  covers={[]}
                  availability="coming_soon"
                  accent={{ bg: "#FFFFFF", primary: "#004D40", text: "#0D1B2A", textMuted: "#5C6470", border: "#E7DFCF" }}
                  releaseInterest={{ seriesId: "lifeiq-level-2", seriesName: level2.name, sourcePage: "books-library-level2" }}
                />
              </div>
            </Reveal>

            <Reveal delay={0.15} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc((100%-48px)/3)]">
              <div id="level3-collection">
                <CollectionCard
                  testId="collection-card-level3"
                  name={LEVEL3_SERIES.name}
                  descriptor="Five Books. One Working System for Real Life."
                  badges={[LEVEL3_SERIES.audience]}
                  covers={[LEVEL3_BOOKS[0], LEVEL3_BOOKS[2], LEVEL3_BOOKS[4]]}
                  format="Digital PDF"
                  availability="available"
                  stat="5 Digital Editions"
                  priceIndicator="From ₹49 · Complete Series ₹249"
                  accent={{ bg: L3.navy, primary: L3.gold, text: "#FFFFFF", textMuted: "#FFFFFF", border: "#2A3C60" }}
                  primaryHref="/books/level3"
                  primaryLabel="Explore Level 3"
                  printInterest={{ seriesId: "level3", sourcePage: "books-library-level3", label: "I'm Interested in Print →" }}
                />
              </div>
            </Reveal>

            <Reveal delay={0.2} className="w-full sm:w-[calc(50%-12px)] lg:w-[calc((100%-48px)/3)]">
              <div id="cyber-safety">
                <CollectionCard
                  testId="collection-card-cyber-safety"
                  name={CYBER_SAFETY_SERIES.name}
                  descriptor={CYBER_SAFETY_SERIES.supporting}
                  badges={CYBER_SAFETY_SERIES.reflex}
                  covers={CYBER_BOOKS.slice(0, 3)}
                  format="Digital PDF"
                  availability="available"
                  stat="3 Digital Editions"
                  priceIndicator="From ₹49 · Complete Series ₹99"
                  accent={{ bg: CC.navy, primary: CC.gold, text: "#FFFFFF", textMuted: "#FFFFFF", border: CC.navyBorder }}
                  primaryHref="/books/cyber-safety"
                  primaryLabel="Explore Cyber Safety"
                  printInterest={{ seriesId: "cyber-safety", sourcePage: "books-library-cyber-safety" }}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTO — numbered editorial chapters (catalogue-neutral) */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] text-[#004D40] font-semibold">The LifeIQ Books manifesto</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[48px] leading-[1.08] text-[#0D1B2A] max-w-3xl">
              Why we make books for real life.
            </h2>
          </Reveal>
          <div className="mt-16">
            {LIFEIQ_BOOKS_MANIFESTO.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div data-testid={`manifesto-chapter-${i + 1}`} className="grid grid-cols-12 gap-6 py-10 border-t border-[#E7DFCF] items-start">
                  <div className="col-span-3 sm:col-span-2 font-serif-d text-[40px] sm:text-[56px] leading-none text-[#F57F17]">{c.n}</div>
                  <div className="col-span-9 sm:col-span-4 font-serif-d text-[22px] sm:text-[26px] leading-[1.2] text-[#0D1B2A]">{c.title}</div>
                  <p className="col-span-12 sm:col-span-6 text-[15px] sm:text-[16px] text-[#5C6470] leading-[1.75]">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PREFER A PRINTED BOOK? — master print-interest section (§14) */}
      <section id="print-interest" className="bg-[#0D1B2A] text-white">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-24 md:py-32 text-center">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">Physical copies</div>
            <h2 className="mt-5 font-serif-d text-[32px] sm:text-4xl lg:text-[46px] leading-[1.1] text-white">
              Prefer a printed book?
            </h2>
            <p className="mt-6 text-[16px] sm:text-[17px] text-white/75 leading-[1.7] max-w-xl mx-auto">
              LifeIQ digital editions are available first. Printed editions are planned based partly on reader demand.
              Tell us which books you'd like in print and we'll let you know when they become available.
            </p>
            <div className="mt-9">
              <PrintInterestButton
                mode="multi"
                sourcePage="books-print-interest-section"
                label="Register Print Interest"
                testId="books-print-interest-section-cta"
                accentColor="#F57F17"
                className="inline-flex items-center justify-center gap-2 bg-[#F57F17] text-white px-8 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-[#D86B0F] transition-colors duration-200 rounded-sm"
              />
            </div>
            <p className="mt-5 text-[12px] uppercase tracking-[0.14em] text-white/40 font-semibold">
              No payment is required. This is not a preorder.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
